import { useEffect, useState, useRef, Fragment } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import RedirectToView from '../../../components/routing/redirectToView';
import { IScreen } from '../../screen/screen';
import useHttpCall, { IHttpCall } from '../../../hooks/useHttpCall';
import useRequestError from '../../../hooks/useRequestError';
import useOverlay from '../../../hooks/useOverlay';
import useLanguage from '../../../hooks/useLanguage';
import apiKeys from '../../../constants/apiKeys';
import { logDebug } from '../../../utils/remoteLogger';
import currentPartner from '../../../constants/partners';

/**
 * SSORedirectView handles the SSO authentication callback for full-page redirects (mobile).
 * This is used when authentication happens via full-page redirect instead of popup window.
 *
 * Flow:
 * 1. User initiates SSO login on mobile -> full page redirects to SSO provider
 * 2. SSO provider redirects to /sso-redirect.html
 * 3. sso-redirect.html redirects to this route with auth parameters
 * 4. This component processes the parameters and logs user in
 * 5. Redirects to the resulting screen using the navigation system
 */
const SSORedirectView = () => {
  const location = useLocation();
  const history = useHistory();
  const handleHttpCall = useHttpCall();
  const handleRequestError = useRequestError();
  const overlay = useOverlay();
  const language = useLanguage();
  const [screenResource, setScreenResource] = useState<IScreen>();
  const processingRef = useRef(false);

  useEffect(() => {
    // Prevent double processing in React strict mode
    if (processingRef.current) {
      return;
    }
    processingRef.current = true;

    const showErrorAndRedirect = (debugMessage: string, error?: Error) => {
      logDebug('SSO redirect', debugMessage, error);
      overlay.presentBasicAlert({
        title: language.get('error'),
        message: language.get('failed_loading_data'),
        buttonTitle: language.get('close'),
        onDismiss: () => {
          // Navigate back to partner root (or generic root if no partner)
          const rootPath = currentPartner?.basePath || '/';
          window.location.replace(rootPath);
        },
      });
    };

    // Retrieve original query params from before SSO redirect
    const originalParams = sessionStorage.getItem('sso_original_params') || '';
    sessionStorage.removeItem('sso_original_params'); // Clean up

    // Merge original params with auth params from SSO provider
    const authParams = new URLSearchParams(location.search);
    const mergedParams = new URLSearchParams(originalParams.replace(/^\?/, ''));

    // Add auth params (they take precedence if there are duplicates)
    authParams.forEach((value, key) => {
      mergedParams.set(key, value);
    });

    // Update current URL with merged params so they're preserved through reinit
    const mergedSearch = mergedParams.toString();
    if (mergedSearch && mergedSearch !== location.search.replace(/^\?/, '')) {
      history.replace(`${location.pathname}?${mergedSearch}`);
    }

    const redirectParams = Object.fromEntries(mergedParams);

    // Retrieve stored loginHttpCall config from sessionStorage
    const storedConfig = sessionStorage.getItem('sso_login_http_call');
    sessionStorage.removeItem('sso_login_http_call'); // Clean up

    if (!storedConfig) {
      showErrorAndRedirect('No stored login config found');
      return;
    }

    let loginHttpCall: IHttpCall;
    try {
      loginHttpCall = JSON.parse(storedConfig);
    } catch (err) {
      showErrorAndRedirect('Failed to parse stored config', err as Error);
      return;
    }

    // Make the login HTTP call with merged parameters
    logDebug('SSO redirect', 'Making SSO login call');
    handleHttpCall<IScreen>(loginHttpCall, {
      body: {
        client: {
          params: {
            ...redirectParams,
          },
          device_info: {
            pub_key: apiKeys.KRY_PUB,
            timestamp: Date.now(),
          },
        },
      },
    })
      .then((screenData) => {
        setScreenResource(screenData);
      })
      .catch((e) => {
        handleRequestError(e);
      });
  }, [
    location.search,
    location.pathname,
    history,
    handleHttpCall,
    handleRequestError,
    overlay,
    language,
  ]);

  // Once we have the screen data, redirect to it using the navigation system
  if (screenResource) {
    return (
      <RedirectToView
        view={{
          type: 'SCREEN',
          hideAppMenu: true,
          source: {
            screen: screenResource,
          },
        }}
      />
    );
  }

  // Show nothing while processing (or could add a loader here if needed)
  return <Fragment />;
};

export default SSORedirectView;
